/**
 * Dettaglio template newsletter — create/edit (RF-03b / §8.4).
 *
 * Ambito del template (§11.7):
 *  - "Template di entità"  → entity_id valorizzato, CRUD gated con NEWSLETTER W sull'entità (useNewsletterAuth).
 *  - "Template standard"    → entity_id = null, gestibile solo dall'admin nazionale.
 *
 * Nota gating standard: il FE non può determinare in modo affidabile l'"admin nazionale"
 * senza un flag dedicato dal BE. Per i template standard il FE consente il tentativo e
 * lascia l'enforcement al backend (403 gestito). In edit, se il BE espone `can_edit`/`can_delete`
 * sul template, questi hanno precedenza.
 *
 * Il corpo viene salvato come prodotto dall'editor (HTML "grezzo"): l'inlining email-safe
 * (juice) è applicato dal composer al momento dell'invio, così il template resta rieditabile.
 *
 * Riferimento: modellata su FilterDetails.js. TDD Sprint 18 §8.4.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Tooltip,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
//--------------------------------------------------------
import { CustomTextField } from '../../../../utils/StyledInputs';
import { EntitiesDialog } from '../../../../utils/EntitiesDialog';
import { DefaultContainedButton, DefaultOutlinedButton, RedOutlinedButton } from '../../../../utils/Buttons';
import { DecisionDialog } from '../../../../utils/DecisionDialog';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { hasNewsletterWrite } from '../../../../hooks/useNewsletterAuth';
import { templatesApi } from '../../../../services/newsletter/templatesApi';
import { mediaApi } from '../../../../services/newsletter/mediaApi';
import { HtmlEditor } from '../components/HtmlEditor/HtmlEditor';
import { CONTENT_PLACEHOLDER, TEMPLATE_INSERTS, hasContentPlaceholder } from '../../../../utils/newsletter/emailHtml';
import { MediaLibraryDialog } from '../components/MediaLibraryDialog';

/** Vincoli allegati (§11.6). NB: il nome del campo multipart è ATTACHMENT_FIELD (da confermare col BE). */
const ATTACH_ALLOWED = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_FIELD = 'file'; // campo multipart atteso da POST /newsletter-templates/{id}/attachments

/** Valida un file: ritorna un messaggio d'errore o null se ok. */
function validateAttachment(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!ATTACH_ALLOWED.includes(ext)) return `"${file.name}": tipo non ammesso`;
  if (file.size > ATTACH_MAX_BYTES) return `"${file.name}": supera i 10MB`;
  return null;
}

/** Formatta una dimensione in byte in stringa leggibile. */
function formatFileSize(size) {
  if (size == null) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** True se l'HTML dell'editor è vuoto (solo markup senza testo/immagini). */
function isHtmlEmpty(html) {
  if (!html) return true;
  const withoutTags = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return withoutTags.length === 0 && !/<img|<hr|<br/i.test(html);
}

export default function NewsletterTemplateDetail() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showPopup, setPopUpText } = usePopup();

  const mode = state?.mode || 'create';
  const templateId = state?.templateId || null;

  const [scopeMode, setScopeMode] = useState('entity'); // 'entity' | 'standard'
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [openEntitiesDialog, setOpenEntitiesDialog] = useState(false);

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isArchived, setIsArchived] = useState(false);

  const [loaded, setLoaded] = useState(null); // template caricato (edit) per leggere eventuali flag can_edit/can_delete
  const [pageLoading, setPageLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Allegati
  const [existingAttachments, setExistingAttachments] = useState([]); // già caricati (edit)
  const [mediaOpen, setMediaOpen] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]); // nuovi, da caricare al salvataggio
  const [removingAttId, setRemovingAttId] = useState(null);
  const fileInputRef = useRef(null);

  const pageTitle = mode === 'create' ? 'Nuovo Template' : 'Modifica Template';

  // -------------------- Caricamento (edit) --------------------
  useEffect(() => {
    if (mode !== 'edit' || !templateId) return;
    templatesApi
      .get(templateId)
      .then((res) => {
        const t = res.data ?? {};
        setLoaded(t);
        setName(t.name ?? '');
        setSubject(t.subject ?? '');
        setBodyHtml(t.body_html ?? '');
        setIsArchived(!!t.is_archived);
        setExistingAttachments(Array.isArray(t.attachments) ? t.attachments : []);
        if (t.entity_id) {
          setScopeMode('entity');
          setSelectedEntity(
            t.entity ?? { id: t.entity_id, name: t.entity_name ?? '', newsletter_auth_type: t.newsletter_auth_type }
          );
        } else {
          setScopeMode('standard');
        }
      })
      .catch((err) => {
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      })
      .finally(() => setPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, templateId]);

  // -------------------- Gating --------------------
  const canWrite = useMemo(() => {
    if (mode === 'edit' && loaded?.can_edit != null) return loaded.can_edit;
    if (scopeMode === 'standard') return true; // enforcement lato BE (403 gestito)
    return hasNewsletterWrite(selectedEntity);
  }, [mode, loaded, scopeMode, selectedEntity]);

  const canDelete = useMemo(() => {
    if (loaded?.can_delete != null) return loaded.can_delete;
    if (scopeMode === 'standard') return true;
    return hasNewsletterWrite(selectedEntity);
  }, [loaded, scopeMode, selectedEntity]);

  // -------------------- Validazione + salvataggio --------------------
  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = true;
    if (!subject.trim()) e.subject = true;
    if (isHtmlEmpty(bodyHtml)) e.body = true;
    if (scopeMode === 'entity' && !selectedEntity?.id) e.entity = true;
    setErrors(e);
    return e;
  };

  const handleApiError = (err) => {
    const status = err?.response?.status;
    if (status === 403) {
      showPopup(false, 'Non hai i permessi per gestire questo template');
    } else if (status === 422) {
      showPopup('warning', err.response?.data?.message || 'Dati non validi');
    } else {
      HttpErrorHandler(err, setPopUpText);
      showPopup(false);
    }
  };

  // -------------------- Allegati --------------------
  // Aggiunge alla lista "staged" i file validi; segnala quelli scartati.
  const handlePickFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = ''; // consente di re-selezionare lo stesso file
    const valid = [];
    const rejected = [];
    picked.forEach((file) => {
      const err = validateAttachment(file);
      if (err) rejected.push(err);
      else valid.push(file);
    });
    if (valid.length) setStagedFiles((prev) => [...prev, ...valid]);
    if (rejected.length) showPopup('warning', rejected.join(' · '));
  };

  const handleRemoveStaged = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Rimozione immediata di un allegato già caricato (solo edit: l'id template esiste).
  const handleRemoveExisting = (att) => {
    setRemovingAttId(att.id);
    templatesApi
      .removeAttachment(templateId, att.id)
      .then(() => {
        setExistingAttachments((prev) => prev.filter((a) => a.id !== att.id));
        showPopup(true, 'Allegato rimosso');
      })
      .catch((err) => handleApiError(err))
      .finally(() => setRemovingAttId(null));
  };

  /**
   * Allega al template un file già in libreria: nessun caricamento, solo il collegamento.
   */
  const handleAttachFromLibrary = (media) => {
    mediaApi
      .attachToTemplate(templateId, media.id)
      .then((res) => {
        setExistingAttachments((prev) =>
          prev.some((a) => a.id === res.data.id) ? prev : [...prev, res.data]
        );
        showPopup(true, res.status === 200 ? 'File già allegato' : 'Allegato aggiunto');
      })
      .catch((err) => handleApiError(err));
  };

  // Carica in sequenza i file "staged" sul template indicato.
  const uploadStagedAttachments = (id) =>
    Promise.all(
      stagedFiles.map((file) => {
        const formData = new FormData();
        formData.append(ATTACHMENT_FIELD, file);
        return templatesApi.addAttachment(id, formData);
      })
    );

  // -------------------- Validazione + salvataggio --------------------
  const handleSave = async () => {
    const e1 = validate();
    if (Object.keys(e1).length > 0) {
      showPopup('warning', 'Inserisci i dati mancanti');
      return;
    }

    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      body_html: bodyHtml, // salvato "grezzo"; inlining email-safe applicato dal composer all'invio
      entity_id: scopeMode === 'entity' ? selectedEntity.id : null,
    };

    const isUpdate = mode === 'edit' && !!templateId;
    setSaving(true);
    try {
      const res = isUpdate ? await templatesApi.update(templateId, payload) : await templatesApi.create(payload);
      // Id del template: in update è templateId, in create arriva dalla risposta (shape difensivo).
      const id = isUpdate
        ? templateId
        : res.data?.id ?? res.data?.template?.id ?? res.data?.data?.id ?? res.data?.newsletter_template?.id;

      // Carica gli allegati appena aggiunti (serve l'id, quindi dopo la create).
      if (id && stagedFiles.length > 0) {
        await uploadStagedAttachments(id);
      }

      showPopup(true, `Template ${isUpdate ? 'aggiornato' : 'creato'} con successo`);
      setTimeout(() => navigate('/admin/newsletter/templates'), 600);
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  // -------------------- Archiviazione --------------------
  const [archiveDialog, setArchiveDialog] = useState(false);

  const handleArchive = () => {
    templatesApi
      .toggleArchive(templateId)
      .then((res) => {
        showPopup(true, res.data?.message || 'Template archiviato');
        setTimeout(() => navigate('/admin/newsletter/templates'), 600);
      })
      .catch((err) => handleApiError(err))
      .finally(() => setArchiveDialog(false));
  };

  if (pageLoading) {
    return (
      <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    );
  }

  const disabled = !canWrite || saving;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Container maxWidth="xl">
        <EntitiesDialog
          openDialog={openEntitiesDialog}
          closeDialog={() => setOpenEntitiesDialog(false)}
          outputEntity={(entity) => setSelectedEntity(entity)}
          context={'newsletter'}
        />

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DefaultOutlinedButton
              onClick={() => navigate('/admin/newsletter/templates')}
              startIcon={<ArrowBackIcon />}
            >
              Indietro
            </DefaultOutlinedButton>
            <Typography variant="h4">{pageTitle}</Typography>
          </Stack>
          {mode === 'edit' && !isArchived && (
            <DefaultOutlinedButton
              onClick={() => setArchiveDialog(true)}
              startIcon={<ArchiveIcon />}
              disabled={!canDelete}
            >
              Archivia
            </DefaultOutlinedButton>
          )}
        </Stack>

        {isArchived && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Questo template è archiviato: non è selezionabile in composizione.
          </Alert>
        )}

        {/* Metadati */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              {/* Ambito: entità vs standard */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#637381' }}>
                  Ambito:
                </Typography>
                <RadioGroup row value={scopeMode} onChange={(e) => setScopeMode(e.target.value)}>
                  <FormControlLabel
                    value="entity"
                    control={<Radio size="small" />}
                    label="Template di entità"
                    disabled={saving}
                  />
                  <FormControlLabel
                    value="standard"
                    control={<Radio size="small" />}
                    label="Template standard"
                    disabled={saving}
                  />
                </RadioGroup>
              </Stack>

              {scopeMode === 'entity' && (
                <CustomTextField
                  label="Entità *"
                  value={selectedEntity ? selectedEntity.name : ''}
                  error={!!errors.entity}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                        {selectedEntity ? (
                          <Tooltip title="Rimuovi" placement="top" arrow>
                            <DeleteIcon onClick={() => !saving && setSelectedEntity(null)} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Seleziona" placement="top" arrow>
                            <EditIcon onClick={() => !saving && setOpenEntitiesDialog(true)} />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {scopeMode === 'entity' && selectedEntity && !canWrite && (
                <Typography color="error" variant="body2">
                  Non hai i permessi per gestire i template di questa entità.
                </Typography>
              )}

              <CustomTextField
                label="Nome template *"
                value={name}
                error={!!errors.name}
                disabled={disabled}
                onChange={(e) => setName(e.target.value)}
                helperText="Identifica il template nella lista (non inviato)"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Contenuto */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <CustomTextField
                fullWidth
                label="Oggetto *"
                value={subject}
                error={!!errors.subject}
                disabled={disabled}
                onChange={(e) => setSubject(e.target.value)}
                helperText="Supporta i placeholder (es. {{nome}})"
              />

              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Corpo del template *
                </Typography>
                {/* Composizione a tre livelli: spiega a chi crea il template cosa ottiene col segnaposto. */}
                <Alert severity={hasContentPlaceholder(bodyHtml) ? 'success' : 'info'} sx={{ mb: 1.5 }}>
                  {hasContentPlaceholder(bodyHtml) ? (
                    <>
                      Template con <strong>spazio contenuto</strong>: chi crea la newsletter scriverà solo il testo che
                      va al posto di <code>{CONTENT_PLACEHOLDER}</code>; il resto del template resta fisso. Deve
                      contenere anche il link di disiscrizione.
                    </>
                  ) : (
                    <>
                      Da &quot;Inserisci campo&quot; aggiungi lo <strong>Spazio contenuto newsletter</strong> per rendere
                      fisso il template e lasciare modificabile solo il contenuto. Senza, il template viene copiato per
                      intero nella newsletter. Cornice grafica e recapiti del mittente sono aggiunti all&apos;invio.
                    </>
                  )}
                </Alert>
                <HtmlEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  disabled={disabled}
                  entityId={selectedEntity?.id ?? null}
                  error={!!errors.body}
                  helperText='Usa "Inserisci campo" per i placeholder dinamici.'
                  extraInserts={TEMPLATE_INSERTS}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Allegati */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Allegati</Typography>
                  <Typography variant="caption" sx={{ color: '#637381' }}>
                    PDF, immagini o documenti Office. Max 10MB per file.
                  </Typography>
                </Box>
                {/* Libreria: disponibile sui template già salvati, dove il collegamento
                    può essere creato subito. In creazione si salva prima il template. */}
                {mode === 'edit' && templateId && (
                  <DefaultOutlinedButton
                    startIcon={<PhotoLibraryIcon />}
                    disabled={disabled}
                    onClick={() => setMediaOpen(true)}
                  >
                    Dalla libreria
                  </DefaultOutlinedButton>
                )}
                <DefaultOutlinedButton
                  startIcon={<AttachFileIcon />}
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Aggiungi allegato
                </DefaultOutlinedButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  accept={ATTACH_ALLOWED.map((e) => `.${e}`).join(',')}
                  onChange={handlePickFiles}
                />
              </Stack>

              {existingAttachments.length === 0 && stagedFiles.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#919EAB' }}>
                  Nessun allegato.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {/* Allegati già caricati (edit) */}
                  {existingAttachments.map((att) => (
                    <Stack key={att.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                      <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: '#637381' }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {att.file_name ?? att.name ?? att.original_name ?? 'Allegato'}
                        </Typography>
                        {att.size != null && (
                          <Typography variant="caption" sx={{ color: '#919EAB' }}>
                            {formatFileSize(att.size)}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Rimuovi" placement="top" arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled || removingAttId === att.id}
                            onClick={() => handleRemoveExisting(att)}
                          >
                            {removingAttId === att.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}

                  {/* File appena aggiunti, da caricare al salvataggio */}
                  {stagedFiles.map((file, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack key={`staged-${index}`} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                      <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: '#637381' }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#919EAB' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <Chip size="small" label="Da caricare" sx={{ mr: 0.5 }} />
                      <Tooltip title="Rimuovi" placement="top" arrow>
                        <span>
                          <IconButton size="small" disabled={saving} onClick={() => handleRemoveStaged(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Azioni */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} justifyContent="flex-end">
          <DefaultOutlinedButton onClick={() => navigate('/admin/newsletter/templates')} disabled={saving}>
            Annulla
          </DefaultOutlinedButton>
          <DefaultContainedButton onClick={handleSave} disabled={disabled} startIcon={<SaveIcon />}>
            {saving ? 'Salvataggio...' : 'Salva template'}
          </DefaultContainedButton>
        </Stack>
      </Container>

      <DecisionDialog
        openDialog={archiveDialog}
        dialogTitle="Archivia template"
        dialogDescription={`Sei sicuro di voler archiviare il template "${name}"? Non sarà più selezionabile in composizione.`}
        closeOperation={() => setArchiveDialog(false)}
        requestOperation={handleArchive}
        requestText="Archivia"
        RequestButton={RedOutlinedButton}
        closeText="Annulla"
      />

      <MediaLibraryDialog
        open={mediaOpen}
        mode="all"
        entityId={selectedEntity?.id ?? null}
        onClose={() => setMediaOpen(false)}
        onSelectMedia={handleAttachFromLibrary}
      />
    </>
  );
}
